import { useEffect, useState } from "react";
import { toDataURL } from "qrcode";

/**
 * Renders a scannable QR code for a TOTP enrolment `otpauth://` URI.
 *
 * Shared between the admin app and (optionally) the main app's own
 * Security page — both enrol the same way against the same
 * `/auth/mfa/enrol` response shape, so the QR rendering belongs here rather
 * than being duplicated per app.
 *
 * Generated entirely client-side with the `qrcode` package (pure JS, no
 * network call, no canvas-measuring third-party service) — the secret this
 * encodes must never leave the browser, and a QR-code-as-a-service API
 * would be exactly that leak. `toDataURL` returns a `data:image/png;...`
 * string that becomes an ordinary `<img>` src; nothing is ever POSTed
 * anywhere to render it.
 *
 * Deliberately NOT the only way to enrol — the component that renders this
 * must keep the setup key (the raw secret) visible and copyable alongside
 * it, since not every authenticator app's camera flow is available (desktop
 * without a webcam, a screen reader, a locked-down work device) and manual
 * entry must keep working on its own.
 */
export function TotpQrCode({
  otpauthUrl,
  size = 200,
}: {
  /** The `otpauth://totp/...` URI returned by `POST /auth/mfa/enrol`. */
  otpauthUrl: string;
  /** Rendered pixel size (square). */
  size?: number;
}) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setDataUrl(null);
    setFailed(false);

    toDataURL(otpauthUrl, { width: size, margin: 1 })
      .then((url) => {
        if (!cancelled) setDataUrl(url);
      })
      .catch(() => {
        // Rendering is a convenience on top of the setup key, which is
        // always shown alongside this component — a failure here (e.g. a
        // malformed URI) must not block manual entry.
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [otpauthUrl, size]);

  if (failed) return null;

  return (
    <div
      role="img"
      aria-label="QR code for authenticator app enrolment — scan it, or use the setup key below instead"
      className="flex items-center justify-center rounded-lg border border-border/50 bg-white p-3"
      style={{ width: size + 24, height: size + 24 }}
    >
      {dataUrl ? (
        <img src={dataUrl} width={size} height={size} alt="" />
      ) : (
        <div
          className="animate-pulse rounded bg-muted"
          style={{ width: size, height: size }}
        />
      )}
    </div>
  );
}
